/**
 * Em que aba o site está — escrito no endereço, e não só na memória do React.
 *
 * Antes a aba morava num `useState` e nada mais. Recarregar a página, apertar
 * F5 no meio de um mapa em edição ou voltar no navegador jogava todo mundo de
 * volta na Escaramuça, com a partida recomeçando do zero: o endereço nunca
 * dizia onde se estava, então não havia para onde voltar.
 *
 * O trecho depois do `#` resolve os três de uma vez. Ele é do cliente — o
 * servidor não o recebe —, então trocar de aba é navegação de verdade, com
 * histórico e sem recarregar nada; e ao abrir de novo, o endereço já diz onde
 * a pessoa estava.
 */

export type AppTab =
  | 'skirmish' | 'campaign' | 'online' | 'shop' | 'community' | 'maps' | 'assets' | 'admin';

/**
 * O nome de cada aba no endereço.
 *
 * Em português porque é o que a pessoa vê e eventualmente compartilha:
 * `oz-emblem.test/#/mapas` diz o que é; `#/maps`, não.
 */
const SLUGS: Readonly<Record<AppTab, string>> = {
  skirmish: 'escaramuca',
  campaign: 'historia',
  online: 'online',
  shop: 'loja',
  community: 'comunidade',
  maps: 'mapas',
  assets: 'acervo',
  admin: 'painel',
};

const BY_SLUG = new Map<string, AppTab>(
  (Object.entries(SLUGS) as [AppTab, string][]).map(([tab, slug]) => [slug, tab]),
);

export const DEFAULT_TAB: AppTab = 'skirmish';

export function tabPath(tab: AppTab): string {
  return `#/${SLUGS[tab]}`;
}

/** A aba que um endereço pede — a padrão quando ele não pede nenhuma. */
export function tabFromHash(hash: string): AppTab {
  const slug = hash.replace(/^#\/?/, '').split(/[/?]/)[0]?.toLowerCase() ?? '';
  return BY_SLUG.get(slug) ?? DEFAULT_TAB;
}

/**
 * Troca a aba no endereço.
 *
 * `pushState` e não `location.hash =`: o segundo dispara `hashchange`, e o
 * componente que acabou de mudar de aba ouviria o próprio eco e se atualizaria
 * duas vezes. Aqui o histórico ganha a entrada e o React fica sabendo pelo
 * caminho normal — pelo estado que ele mesmo mudou.
 */
export function pushTab(tab: AppTab): void {
  const target = tabPath(tab);
  if (window.location.hash === target) return;
  window.history.pushState(null, '', target);
}

/** Deixa o endereço combinar com a aba atual sem criar entrada no histórico. */
export function replaceTab(tab: AppTab): void {
  const target = tabPath(tab);
  if (window.location.hash === target) return;
  window.history.replaceState(null, '', target);
}
